import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaExclamationTriangle,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaTag,
  FaImage,
  FaTimes,
  FaBox,
  FaUpload,
  FaCloudUploadAlt,
  FaSpinner
} from 'react-icons/fa';
import { productService, categoryService, authService } from '../../../../services/apiService';
import { getApiErrorMessage } from '../../../../utils/api';
import { toast } from 'react-toastify';
import HierarchicalCategorySelector from '../../admin/components/HierarchicalCategorySelector';
import VehicleCompatibilitySelector from '../../../Vehicles/VehicleCompatibilitySelector';
import CatalogFitmentSelector, { emptyCatalogVehiclePath } from '../../../Vehicles/CatalogFitmentSelector';
import UsedGradeCardSelector from '../../../vendor/UsedGradeCardSelector';
import {
  getMinPhotosForGrade,
  gradeToCondition,
  conditionToGrade,
  getGradeMeta,
} from '../../../../utils/usedPartGrades';
import { canUseCameraFirstListing } from '../../../../utils/partListingShots';

/** Shown when category has no admin template — required for deal creation. */
const DEAL_FALLBACK_SPEC_FIELDS = [
  { key: 'productSpecification', label: 'Product specification / details', type: 'text' },
  { key: 'compatibilityNotes', label: 'Compatibility / fitment notes', type: 'text' },
];

const emptyVehicleCompatRow = () => ({
  make: '',
  model: '',
  yearFrom: '',
  yearTo: '',
  variant: 'All',
  fuelType: 'All',
});

function hasValidVehicleCompatibility(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((vc) => {
    const make = String(vc?.make || '').trim();
    const model = String(vc?.model || '').trim();
    const yearFrom = String(vc?.yearFrom || '').trim();
    return make && model && yearFrom;
  });
}

function buildVehicleCompatibilityFromCatalog(path, generationMeta) {
  if (!path?.make || !path?.model) return [];
  const yearFrom =
    generationMeta?.yearStart != null ? String(generationMeta.yearStart) : '';
  const yearTo =
    generationMeta?.yearEnd != null ? String(generationMeta.yearEnd) : yearFrom;
  return [
    {
      make: path.make,
      model: path.model,
      yearFrom,
      yearTo,
      variant: path.variant || 'All',
      fuelType: path.fuelType || 'All',
    },
  ];
}

const Products = ({ products, searchTerm, setSearchTerm, onProductAdded }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cameraFirstListing = canUseCameraFirstListing(user);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [specFields, setSpecFields] = useState([]);
  const [specTemplateId, setSpecTemplateId] = useState('generic');
  const emptyUsed = useCallback(
    () => ({
      condition: '',
      usageKm: '',
      usageMonths: '',
      originalVehicle: '',
      damageNotes: '',
      sellerType: '',
    }),
    []
  );
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    subCategory: '',
    price: '',
    mrp: '',
    stock: '',
    description: '',
    brand: '',
    brandIsOem: false,
    partNumber: '',
    partType: 'AUTO',
    class: 'Vehicle-specific',
    vehicleCompatibility: [],
    catalogVehiclePath: emptyCatalogVehiclePath(),
    specs: {},
    usedPartDetails: emptyUsed(),
    usedGrade: '',
    warranty: '',
    deliveryTime: '',
    images: [],
    minOrderQty: '1',
  });
  const [catalogResolve, setCatalogResolve] = useState(null);
  const [catalogGenerationMeta, setCatalogGenerationMeta] = useState(null);

  // Fetch categories from backend
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      // Use flat categories endpoint which returns all categories created by admin from superadmin panel
      const result = await categoryService.getAllActiveCategoriesFlat({ forVendor: true });
      
      // Handle response structure:
      // Backend returns: { success: true, data: { categories: [...] } }
      // apiService.getAllActiveCategoriesFlat() returns: response.data.data which is { categories: [...] }
      // So: result = { categories: [...] }
      // Extract: result.categories to get the array
      let categoriesData = [];
      
      if (result && result.categories && Array.isArray(result.categories)) {
        categoriesData = result.categories;
      } else if (Array.isArray(result)) {
        categoriesData = result;
      } else if (result && result.data && Array.isArray(result.data.categories)) {
        categoriesData = result.data.categories;
      } else if (result && result.data && Array.isArray(result.data)) {
        categoriesData = result.data;
      }
      
      // Extract category names from the flat list (categories created by admin)
      const categoryNames = categoriesData
        .map(cat => {
          // Handle both string and object formats
          if (typeof cat === 'string') {
            return cat;
          } else if (cat && cat.name) {
            return cat.name;
          }
          return null;
        })
        .filter(name => name && name.trim() !== '') // Filter out empty names
        .sort(); // Sort alphabetically
      
      // If no categories found in database (created by admin), show empty or use fallback
      if (categoryNames.length === 0) {
        console.warn('No categories found in database (created by admin), using fallback categories');
        // Only use fallback if absolutely necessary - admin should create categories
        const fallbackCategories = [
          'Maintenance Service Parts',
          'Brake System',
          'Air Conditioning',
          'Body',
          'Bearings',
          'Belts Chains And Rollers',
          'Car Accessories',
          'Clutch',
          'Control Cables',
          'Electrical Components',
          'Engine',
          'Engine Cooling System',
          'Exhaust System',
          'Filters',
          'Fuel Supply System',
          'Gaskets & Seals',
          'Ignition & Glowplug System',
          'Interior Comfort',
          'Lighting',
          'Oils & Fluids',
          'Pipes & Hoses',
          'Repair Kits',
          'Sensors Relay and Control Units',
          'Steering',
          'Suspension and Arms',
          'Towbar Parts',
          'Trims',
          'Tyres and Alloys',
          'Transmission',
          'Universal',
          'Wheels',
        ];
        setCategories(fallbackCategories);
      } else {
        // Use categories created by admin from superadmin panel
        setCategories(categoryNames);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories. Using default list.');
      setCategories([
        'Maintenance Service Parts', 'Brake System', 'Engine', 'Filters', 'Electrical Components',
        'Lighting', 'Suspension and Arms', 'Transmission', 'Universal', 'Oils & Fluids'
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };
  const productClasses = ['Universal', 'Vehicle-specific'];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!formData.category) {
        setSpecFields([]);
        setSpecTemplateId('generic');
        return;
      }
      try {
        const data = await productService.getProductSpecTemplate({
          category: formData.category,
          subCategory: formData.subCategory,
        });
        if (cancelled) return;
        setSpecFields(data?.fields || []);
        setSpecTemplateId(data?.templateId || 'generic');
      } catch {
        if (!cancelled) {
          setSpecFields([]);
          setSpecTemplateId('generic');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.category, formData.subCategory]);

  const effectiveSpecFields =
    specFields.length > 0
      ? specFields
      : formData.category
        ? DEAL_FALLBACK_SPEC_FIELDS
        : [];

  const handleAddProduct = () => {
    setCatalogResolve(null);
    setCatalogGenerationMeta(null);
    setFormData({
      name: '',
      sku: '',
      category: '',
      subCategory: '',
      price: '',
      mrp: '',
      stock: '',
      description: '',
      brand: '',
      brandIsOem: false,
      partNumber: '',
      partType: 'AUTO',
      class: 'Vehicle-specific',
      vehicleCompatibility: [],
      catalogVehiclePath: emptyCatalogVehiclePath(),
      specs: {},
      usedPartDetails: emptyUsed(),
      usedGrade: '',
      warranty: '',
      deliveryTime: '',
      images: [],
      minOrderQty: '1',
    });
    setShowAddModal(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    const remainingSlots = 5 - formData.images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.warning(`Maximum 5 images allowed. Only ${remainingSlots} image(s) will be added.`);
    }

    if (filesToProcess.length === 0) {
      toast.warning('Maximum 5 images allowed per product');
      return;
    }

    toast.info('Compressing & uploading images…', { autoClose: 2000 });

    const uploaded = [];
    for (const file of filesToProcess) {
      try {
        const url = await authService.uploadCompressedFile(file);
        uploaded.push(url);
      } catch (error) {
        console.error('Image upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploaded.length > 0) {
      setFormData({ ...formData, images: [...formData.images, ...uploaded] });
      toast.success(`${uploaded.length} image(s) added successfully`);
    }

    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({...formData, images: newImages});
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    // vehicleCompatibility from API is an array of objects {make, model, yearFrom, yearTo, variant, fuelType}
    // Keep as-is for the VehicleCompatibilitySelector; fallback to empty array if not present
    const vehicleCompatibility = Array.isArray(product.vehicleCompatibility)
      ? product.vehicleCompatibility.map(v =>
          typeof v === 'object' && v !== null
            ? { make: v.make || '', model: v.model || '', yearFrom: v.yearFrom || '', yearTo: v.yearTo || '', variant: v.variant || 'All', fuelType: v.fuelType || 'All' }
            : { make: String(v), model: '', yearFrom: '', yearTo: '', variant: 'All', fuelType: 'All' }
        )
      : [];
    
    const specs =
      product.specs && typeof product.specs === 'object' && !Array.isArray(product.specs)
        ? { ...product.specs }
        : {};
    const pt = product.partType
      ? product.partType
      : product.condition === 'used'
        ? 'USED'
        : 'AUTO';
    const ud = product.usedPartDetails || {};
    const usedGrade =
      product.usedGrade || conditionToGrade(ud.condition) || '';
    setFormData({
      name: product.name || '',
      sku: product.sku || '',
      category: product.category || '',
      subCategory: product.subCategory || product.subcategory || '',
      price: product.price || '',
      mrp: product.mrp || '',
      stock: product.stock || '',
      description: product.description || '',
      brand: product.brand || '',
      brandIsOem: !!product.brandIsOem,
      partNumber: product.partNumber || '',
      partType: ['NEW', 'AFTERMARKET', 'USED'].includes(pt) ? pt : 'AUTO',
      class: product.class || 'Universal',
      vehicleCompatibility: vehicleCompatibility,
      specs,
      usedPartDetails: {
        condition: ud.condition || '',
        usageKm: ud.usageKm ?? '',
        usageMonths: ud.usageMonths ?? '',
        originalVehicle: ud.originalVehicle || '',
        damageNotes: ud.damageNotes || '',
        sellerType: ud.sellerType || '',
      },
      usedGrade,
      warranty: product.warranty || '',
      deliveryTime: product.deliveryTime || '',
      images: product.images || [],
      minOrderQty: product.minOrderQty || '1',
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!formData.name?.trim()) {
        toast.error('Part name is required');
        return;
      }
      if (!formData.partNumber?.trim()) {
        toast.error('Part number is required');
        return;
      }
      if (!formData.category?.trim()) {
        toast.error('Category is required');
        return;
      }
      if (!formData.brand?.trim()) {
        toast.error('Brand is required');
        return;
      }
      if (!formData.description?.trim()) {
        toast.error('Description is required');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        toast.error('Valid price is required');
        return;
      }

      const mrpVal = formData.mrp !== '' && formData.mrp != null ? parseFloat(formData.mrp) : null;
      if (mrpVal != null && !isNaN(mrpVal) && mrpVal < price) {
        toast.error(
          `MRP (₹${mrpVal.toLocaleString('en-IN')}) must be ≥ selling price (₹${price.toLocaleString('en-IN')}). Leave MRP blank if not applicable.`
        );
        return;
      }

      if (formData.class === 'Vehicle-specific') {
        if (!catalogResolve?.catalogNodeId) {
          toast.error(
            'Complete catalog fitment (make, model, generation) — catalog node must resolve before publishing'
          );
          return;
        }
      }

      const isUsed = formData.partType === 'USED';
      if (!isUsed) {
        const st = parseInt(formData.stock, 10);
        if (isNaN(st) || st < 0) {
          toast.error('Valid stock quantity is required');
          return;
        }
      }

      if (isUsed) {
        if (!['A', 'B', 'C'].includes(formData.usedGrade)) {
          toast.error('Please select a used part grade (A, B, or C)');
          return;
        }
        const minPhotos = getMinPhotosForGrade(formData.usedGrade);
        if (!formData.images?.length || formData.images.length < minPhotos) {
          const meta = getGradeMeta(formData.usedGrade);
          toast.error(
            `Grade ${formData.usedGrade} (${meta.label}) requires at least ${minPhotos} photos`
          );
          return;
        }
        const km = formData.usedPartDetails.usageKm;
        const mo = formData.usedPartDetails.usageMonths;
        const hasKm = km !== '' && km !== undefined && !isNaN(Number(km)) && Number(km) >= 0;
        const hasMo = mo !== '' && mo !== undefined && !isNaN(Number(mo)) && Number(mo) >= 0;
        if (!hasKm && !hasMo) {
          toast.error('Enter usage in KM or months for used parts');
          return;
        }
      }

      if (effectiveSpecFields.length) {
        for (const f of effectiveSpecFields) {
          const v = formData.specs[f.key];
          if (v === undefined || v === null || String(v).trim() === '') {
            toast.error(`Please enter ${f.label}`);
            return;
          }
        }
      } else if (!formData.category?.trim()) {
        toast.error('Select a category first — specification fields load after category is chosen.');
        return;
      }

      const productData = {
        name: formData.name.trim(),
        partNumber: formData.partNumber.trim(),
        category: formData.category.trim(),
        subCategory: formData.subCategory?.trim() || undefined,
        brand: formData.brand.trim(),
        brandIsOem: !!formData.brandIsOem,
        partType: formData.partType,
        price,
        class: formData.class || 'Universal',
        stock: isUsed ? 1 : parseInt(formData.stock, 10) || 0,
        minOrderQty: parseInt(formData.minOrderQty, 10) || 1,
        vehicleCompatibility: formData.vehicleCompatibility || [],
        deliveryTime: formData.deliveryTime?.trim() || undefined,
        description: formData.description.trim(),
        specs: formData.specs || {},
      };

      if (formData.class === 'Vehicle-specific' && catalogResolve?.catalogNodeId) {
        const path = formData.catalogVehiclePath || emptyCatalogVehiclePath();
        productData.catalogVehiclePath = path;
        productData.partCategory = formData.name.trim();
        productData.subSystem = catalogResolve.subSystem;
        productData.vehicleCompatibility = buildVehicleCompatibilityFromCatalog(
          path,
          catalogGenerationMeta
        );
      }

      if (formData.sku?.trim()) productData.sku = formData.sku.trim();
      if (formData.mrp && !isNaN(parseFloat(formData.mrp))) productData.mrp = parseFloat(formData.mrp);
      if (formData.warranty?.trim()) productData.warranty = formData.warranty.trim();

      if (formData.images?.length) {
        const totalSize = formData.images.reduce((sum, img) => sum + (img.length * 0.75), 0);
        if (totalSize > 50 * 1024 * 1024) {
          toast.warning('Images are large. This may take a moment...');
        }
        productData.images = formData.images;
      }

      if (isUsed) {
        productData.usedGrade = formData.usedGrade;
        productData.usedPartDetails = {
          condition: gradeToCondition(formData.usedGrade),
          usageKm:
            formData.usedPartDetails.usageKm === '' || formData.usedPartDetails.usageKm === undefined
              ? undefined
              : Number(formData.usedPartDetails.usageKm),
          usageMonths:
            formData.usedPartDetails.usageMonths === '' ||
            formData.usedPartDetails.usageMonths === undefined
              ? undefined
              : Number(formData.usedPartDetails.usageMonths),
          originalVehicle: formData.usedPartDetails.originalVehicle?.trim() || undefined,
          damageNotes: formData.usedPartDetails.damageNotes?.trim() || undefined,
          sellerType: formData.usedPartDetails.sellerType || undefined,
        };
      }

      if (showAddModal) {
        const created = await productService.createVendorProduct(productData);
        const listingStatus = created?.product?.listingStatus || created?.listingStatus;
        if (listingStatus === 'pending_admin') {
          toast.info(
            'Electrical part (Profile E) submitted — waiting for admin catalog approval before it appears in search.'
          );
        } else {
          toast.success('Product added successfully! Waiting for admin approval.');
        }
      } else if (showEditModal && selectedProduct) {
        // Update existing product
        const productId = selectedProduct._id || selectedProduct.id;
        await productService.updateVendorProduct(productId, productData);
        toast.success('Product updated successfully!');
      }
      
      // Close modals and reset form
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedProduct(null);
      setCatalogResolve(null);
      setCatalogGenerationMeta(null);
      
      // Reset form data
      setFormData({
        name: '',
        sku: '',
        category: '',
        subCategory: '',
        price: '',
        mrp: '',
        stock: '',
        description: '',
        brand: '',
        brandIsOem: false,
        partNumber: '',
        partType: 'AUTO',
        class: 'Vehicle-specific',
        vehicleCompatibility: [],
        catalogVehiclePath: emptyCatalogVehiclePath(),
        specs: {},
        usedPartDetails: emptyUsed(),
        usedGrade: '',
        warranty: '',
        deliveryTime: '',
        images: [],
        minOrderQty: '1',
      });
      
      // Refresh product list
      if (onProductAdded) {
        onProductAdded();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save product. Please check price and MRP.'));
      console.error('Error saving product:', error);
    }
  };

  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    const matchesSearch = !searchTerm || 
      (product?.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product?.sku ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const productCategory = (product?.category ?? '').toString().trim();
    const selectedCategory = (filterCategory ?? '').toString().trim();
    const subCat = (product?.subCategory ?? '').toString().trim();
    const matchesCategory =
      filterCategory === 'all' ||
      productCategory.toLowerCase() === selectedCategory.toLowerCase() ||
      subCat.toLowerCase() === selectedCategory.toLowerCase();
    
    const productStatusValue = product?.status || (product?.approved ? 'approved' : 'pending');
    const stock = product?.stock ?? 0;
    const productStatus =
      productStatusValue === 'rejected'
        ? 'Rejected'
        : productStatusValue === 'approved'
          ? stock > 0
            ? 'Active'
            : 'Out of Stock'
          : 'Pending Approval';
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && productStatus === 'Active') ||
      (filterStatus === 'out' && productStatus === 'Out of Stock') ||
      (filterStatus === 'low' && stock > 0 && stock < 20) ||
      (filterStatus === 'pending' && productStatus === 'Pending Approval');
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Supply Automotive Parts & Equipment</h3>
          <p className="text-sm text-gray-600 mt-1">Manage your product catalog and inventory</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/vendor/dashboard/listings/new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-600 text-emerald-700 font-semibold text-sm hover:bg-emerald-50"
          >
            Quick list (3-click)
          </button>
          <button
            type="button"
            onClick={() =>
              cameraFirstListing
                ? navigate('/vendor/dashboard/parts/add')
                : handleAddProduct()
            }
            className="btn-primary flex items-center gap-2"
          >
            <FaPlus /> {cameraFirstListing ? 'Add Part' : 'Add New Product'}
          </button>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product name, SKU, or part number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="relative">
            <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
              disabled={loadingCategories}
            >
              <option value="all">All Categories</option>
              {loadingCategories ? (
                <option disabled>Loading categories...</option>
              ) : (
                categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))
              )}
            </select>
            {loadingCategories && (
              <FaSpinner className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending Approval</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">SKU / Part #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Availability</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Sales</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map((product, pidx) => {
                const isApproved = product.status === 'approved' || product.approved;
                const isRejected = product.status === 'rejected';
                return (
                <tr key={product.id ?? product._id ?? pidx} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.images?.length > 0 ? (
                        <img loading="lazy" 
                          src={product.images[0]} 
                          alt={product.name ?? 'Product'} 
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center ${product.images?.length > 0 ? 'hidden' : ''}`}>
                        <FaImage className="text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{product.name ?? '—'}</p>
                        <p className="text-xs text-gray-500">{product.brand ?? 'No Brand'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm text-gray-700 font-mono">{product.sku ?? '—'}</p>
                      {product.partNumber && (
                        <p className="text-xs text-gray-500">PN: {product.partNumber}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      <FaTag className="text-xs" />
                      {[product.category, product.subCategory].filter(Boolean).join(' › ') || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${
                        (product?.stock ?? 0) === 0 ? 'text-red-600' :
                        (product?.stock ?? 0) < 20 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {product?.stock ?? 0}
                      </span>
                      {(product?.stock ?? 0) < 20 && (product?.stock ?? 0) > 0 && (
                        <FaExclamationTriangle className="text-orange-500 text-xs" />
                      )}
                      {(product?.stock ?? 0) === 0 && (
                        <FaTimesCircle className="text-red-500 text-xs" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">₹{(Number(product?.price) || 0).toLocaleString()}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      isApproved && (product?.stock ?? 0) > 0
                        ? 'bg-green-100 text-green-800'
                        : isRejected
                        ? 'bg-red-100 text-red-800'
                        : !isApproved
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {isApproved && product.stock > 0 ? (
                        <>
                          <FaCheckCircle className="text-xs" />
                          Available
                        </>
                      ) : isRejected ? (
                        <>
                          <FaTimesCircle className="text-xs" />
                          Rejected
                        </>
                      ) : !isApproved ? (
                        <>
                          <FaExclamationTriangle className="text-xs" />
                          Pending Approval
                        </>
                      ) : (
                        <>
                          <FaTimesCircle className="text-xs" />
                          Unavailable
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-600">{product.sales || 0}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEditProduct(product)}
                        className="text-blue-600 hover:text-blue-800 p-1" 
                        title="Edit Product"
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-800 p-1" 
                        title="View Details"
                      >
                        <FaEye className="text-sm" />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-800 p-1" 
                        title="Delete"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <FaBox className="text-gray-300 text-4xl mx-auto mb-3" />
            <p className="text-gray-600">No products found</p>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-2xl font-bold text-gray-900">
                {showAddModal ? 'Add New Product' : 'Edit Product'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1" style={{ overflowX: 'visible' }}>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                    1. Basic info
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Part name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. Engine oil 5W30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Part number *</label>
                      <input
                        type="text"
                        required
                        value={formData.partNumber}
                        onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="OEM / manufacturer part number"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <HierarchicalCategorySelector
                        category={formData.category}
                        onChange={({ category, subCategory }) =>
                          setFormData((prev) => ({ ...prev, category, subCategory, specs: {} }))
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Brand *</label>
                      <input
                        type="text"
                        required
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. Bosch, Honda Genuine"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.brandIsOem}
                          onChange={(e) => setFormData({ ...formData, brandIsOem: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        OEM / genuine brand (helps auto-classify as New)
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
                      <select
                        required
                        value={formData.class}
                        onChange={(e) => {
                          const nextClass = e.target.value;
                          setFormData((prev) => {
                            const next = { ...prev, class: nextClass };
                            if (
                              nextClass === 'Vehicle-specific' &&
                              !hasValidVehicleCompatibility(prev.vehicleCompatibility) &&
                              (!Array.isArray(prev.vehicleCompatibility) || prev.vehicleCompatibility.length === 0)
                            ) {
                              next.vehicleCompatibility = [emptyVehicleCompatRow()];
                            }
                            return next;
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {productClasses.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                    2. Pricing & inventory
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">MRP (₹) — optional</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.mrp}
                        onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="For discount %"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank if unknown. If set, MRP must be ≥ selling price (shows discount to buyers).
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stock quantity * {formData.partType === 'USED' ? <span className="text-gray-500 font-normal">(used = 1)</span> : null}
                      </label>
                      <input
                        type="number"
                        required={formData.partType !== 'USED'}
                        disabled={formData.partType === 'USED'}
                        min="0"
                        value={formData.partType === 'USED' ? '1' : formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SKU — optional</label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Internal SKU"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Min order qty</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.minOrderQty}
                        onChange={(e) => setFormData({ ...formData, minOrderQty: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Products with stock 0 are hidden from the public catalogue.</p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                    3. Part type
                  </h4>
                  <div className="space-y-2">
                    {[
                      { v: 'AUTO', label: 'Auto (from brand / OEM flag)' },
                      { v: 'NEW', label: 'New (OEM-style)' },
                      { v: 'AFTERMARKET', label: 'Aftermarket' },
                      { v: 'USED', label: 'Used' },
                    ].map((opt) => (
                      <label key={opt.v} className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                        <input
                          type="radio"
                          name="partType"
                          value={opt.v}
                          checked={formData.partType === opt.v}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              partType: opt.v,
                              stock: opt.v === 'USED' ? '1' : prev.stock,
                              usedGrade: opt.v === 'USED' ? prev.usedGrade : '',
                            }))
                          }
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                    4. Specifications <span className="text-red-600 normal-case font-semibold">(required for deals)</span>
                  </h4>
                  {!formData.category ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Select a <strong>category</strong> in section 1 first — specification fields appear here.
                    </p>
                  ) : specFields.length === 0 ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                      No category template — fill the fields below so this product can be used in <strong>Deals</strong>.
                    </p>
                  ) : null}
                  {effectiveSpecFields.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {effectiveSpecFields.map((f) => (
                        <div key={f.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{f.label} *</label>
                          {f.type === 'select' && f.options ? (
                            <select
                              required
                              value={formData.specs[f.key] || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  specs: { ...formData.specs, [f.key]: e.target.value },
                                })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select…</option>
                              {f.options.map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              required
                              value={formData.specs[f.key] || ''}
                              placeholder={f.placeholder || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  specs: { ...formData.specs, [f.key]: e.target.value },
                                })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {formData.partType === 'USED' && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                      5. Used part grade *
                    </h4>
                    <UsedGradeCardSelector
                      value={formData.usedGrade}
                      onChange={({ usedGrade, condition }) =>
                        setFormData((prev) => ({
                          ...prev,
                          usedGrade,
                          usedPartDetails: { ...prev.usedPartDetails, condition },
                        }))
                      }
                    />
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                    {formData.partType === 'USED' ? '6. Media & description' : '5. Media & description'}
                  </h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <FaCloudUploadAlt className="text-4xl text-gray-400" />
                      <div className="text-center">
                        <label htmlFor="image-upload" className="cursor-pointer">
                          <span className="btn-primary inline-flex items-center gap-2">
                            <FaUpload /> Upload images
                          </span>
                        </label>
                        <input
                          id="image-upload"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          {formData.partType === 'USED' ? (
                            formData.usedGrade ? (
                              <>
                                Grade {formData.usedGrade}: minimum{' '}
                                <strong>{getMinPhotosForGrade(formData.usedGrade)} photos</strong> required
                                {formData.images.length > 0 && (
                                  <>
                                    {' '}
                                    ({formData.images.length}/
                                    {getMinPhotosForGrade(formData.usedGrade)} uploaded)
                                  </>
                                )}
                                .{' '}
                              </>
                            ) : (
                              'Select a grade above to see photo requirements. '
                            )
                          ) : (
                            'Recommended. '
                          )}
                          PNG, JPG up to 10MB each (max 5).
                        </p>
                        {formData.partType === 'USED' && formData.usedGrade && (
                          <p className="text-[11px] text-amber-700 mt-1 max-w-md mx-auto">
                            {getGradeMeta(formData.usedGrade).photoRequirements}
                          </p>
                        )}
                      </div>
                      {formData.images.length > 0 && (
                        <div className="grid grid-cols-4 gap-4 w-full mt-4">
                          {formData.images.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                loading="lazy"
                                src={image}
                                alt={`Product ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-300"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                    <textarea
                      rows={4}
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Fitment notes, packaging, what is included…"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Warranty — optional</label>
                    <input
                      type="text"
                      value={formData.warranty}
                      onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. 12 months"
                    />
                  </div>
                </div>

                {formData.partType === 'USED' && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                      7. Used part details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Seller type</label>
                        <select
                          value={formData.usedPartDetails.sellerType}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              usedPartDetails: { ...formData.usedPartDetails, sellerType: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">—</option>
                          <option value="garage">Garage</option>
                          <option value="vendor">Vendor</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Usage (KM)</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.usedPartDetails.usageKm}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              usedPartDetails: { ...formData.usedPartDetails, usageKm: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional if months set"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Usage (months)</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.usedPartDetails.usageMonths}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              usedPartDetails: { ...formData.usedPartDetails, usageMonths: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional if KM set"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Original vehicle — optional</label>
                        <input
                          type="text"
                          value={formData.usedPartDetails.originalVehicle}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              usedPartDetails: { ...formData.usedPartDetails, originalVehicle: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g. 2019 Swift VDI"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Damage notes</label>
                        <textarea
                          rows={2}
                          value={formData.usedPartDetails.damageNotes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              usedPartDetails: { ...formData.usedPartDetails, damageNotes: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.class === 'Vehicle-specific' ? (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
                    <h4 className="text-sm font-semibold text-indigo-900 mb-1">
                      Catalog fitment (7-layer) *
                    </h4>
                    <p className="text-xs text-indigo-800 mb-3">
                      Select make → model → generation. The system detects the part profile from the part
                      name and resolves a shared <code className="text-[10px]">catalogNodeId</code> for
                      new and used listings on the same node.
                    </p>
                    <CatalogFitmentSelector
                      value={formData.catalogVehiclePath}
                      partCategory={formData.name}
                      onChange={(path) => setFormData({ ...formData, catalogVehiclePath: path })}
                      onResolved={setCatalogResolve}
                      onGenerationMeta={setCatalogGenerationMeta}
                    />
                  </div>
                ) : (
                  <details className="rounded-lg border border-gray-200 p-4">
                    <summary className="text-sm font-medium text-gray-800 cursor-pointer">
                      Optional: manual vehicle compatibility
                    </summary>
                    <p className="text-xs text-gray-500 mt-2 mb-3">
                      Prefer category specs above; use this only when you need explicit make/model rows.
                    </p>
                    <VehicleCompatibilitySelector
                      value={
                        Array.isArray(formData.vehicleCompatibility) &&
                        formData.vehicleCompatibility.length > 0 &&
                        typeof formData.vehicleCompatibility[0] === 'object'
                          ? formData.vehicleCompatibility
                          : []
                      }
                      onChange={(val) => setFormData({ ...formData, vehicleCompatibility: val })}
                    />
                  </details>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery time — optional</label>
                  <input
                    type="text"
                    value={formData.deliveryTime}
                    onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 3–5 business days"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedProduct(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {showAddModal ? 'Add Product' : 'Update Product'}
                  </button>
                </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;

